import { useRef, useState } from "react";
import { Download, FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { euro } from "@/lib/shop";

const HEADER = "nome_articolo,categoria,prezzo,quantita,marca,lingua,tipo,peso_g,lunghezza_cm,larghezza_cm,altezza_cm,immagine_fronte_url,immagine_retro_url,descrizione,espansione,numero_carta,rarita,anno,tiktok_url";
const EXAMPLE = [
  "Pikachu 025/165,Pokémon,2.50,3,Pokémon,ITA,Singolo,15,15,10,1,,,,151,025/165,Comune,2023,",
  "OP-01 Romance Dawn Booster Box,One Piece,99.90,1,Bandai,JP,Box/Display,450,20,12,8,,,Box sigillata da 24 buste,,,,2022,",
];
const BATCH = 20;

const downloadTemplate = () => {
  const blob = new Blob(["\uFEFF" + [HEADER, ...EXAMPLE].join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "modello-articoli-mcanime.csv" });
  a.click();
  URL.revokeObjectURL(a.href);
};

const DropZone = ({ onFile, busy }) => {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      data-testid="csv-dropzone"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files?.[0]); }}
      onClick={() => input.current?.click()}
      className={`grid cursor-pointer place-items-center rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${over ? "border-[#A64DFF] bg-violet-50" : "border-[#0b0b0f] bg-[#fffbe6] hover:bg-[#fff6c2]"}`}
    >
      {busy ? <Loader2 className="h-8 w-8 animate-spin" /> : <FileUp className="h-8 w-8" />}
      <div className="mt-2 text-sm font-bold">Trascina qui il file .csv o tocca per sceglierlo</div>
      <div className="text-xs text-muted-foreground">UTF-8, separatore virgola o punto e virgola</div>
      <input ref={input} data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden"
        onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
};

const PreviewTable = ({ rows }) => (
  <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-xl border border-border">
    <table data-testid="csv-preview-table" className="w-full min-w-[640px] text-left text-xs">
      <thead className="bg-muted"><tr>{["Riga", "Nome", "Categoria", "Prezzo", "Qtà", "Lingua", "Tipo", "Foto", "Stato"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.line} data-testid={`csv-row-${r.line}`} data-error={r.errors.length ? "true" : "false"} className={r.errors.length ? "bg-red-50 text-red-800" : ""}>
            <td className="px-2 py-1.5">{r.line}</td>
            <td className="max-w-[180px] truncate px-2 py-1.5 font-semibold">{r.data.name || "—"}</td>
            <td className="px-2 py-1.5">{r.data.category || "—"}</td>
            <td className="px-2 py-1.5">{r.data.price ? euro(r.data.price) : "—"}</td>
            <td className="px-2 py-1.5">{r.data.quantity}</td>
            <td className="px-2 py-1.5">{r.data.language || "—"}</td>
            <td className="px-2 py-1.5">{r.data.condition || "—"}</td>
            <td className="px-2 py-1.5">{r.data.image_urls?.length || 0}</td>
            <td className="px-2 py-1.5">{r.errors.length ? r.errors.join("; ") : "OK"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ImportCsvDialog = ({ onClose, onImported }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) return toast.error("Carica un file .csv");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(errMsg(e, "File non leggibile"));
    } finally {
      setBusy(false);
    }
  };

  const valid = preview?.rows.filter((r) => !r.errors.length) || [];
  const errorRows = preview?.rows.filter((r) => r.errors.length) || [];

  const runImport = async () => {
    const batchId = crypto.randomUUID();
    const sum = { created: 0, skipped: 0, errors: [] };
    setProgress(0);
    for (let i = 0; i < valid.length; i += BATCH) {
      const rows = valid.slice(i, i + BATCH).map(({ line, data }) => ({ line, data }));
      try {
        const { data } = await api.post("/admin/products/import", { batch_id: batchId, rows });
        sum.created += data.created;
        sum.skipped += data.skipped;
        sum.errors.push(...data.errors);
      } catch (e) {
        sum.errors.push(...rows.map((r) => ({ line: r.line, error: errMsg(e, "Errore di rete") })));
      }
      setProgress(Math.round((Math.min(i + BATCH, valid.length) / valid.length) * 100));
    }
    setResult(sum);
    setProgress(null);
    if (sum.created) onImported();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && progress === null && onClose()}>
      <DialogContent data-testid="csv-import-dialog" className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold">Importa articoli da CSV</DialogTitle>
          <DialogDescription>Carica un file .csv, controlla l'anteprima e conferma l'importazione.</DialogDescription></DialogHeader>
        <button data-testid="csv-download-template" className="btn-ghost-mc w-fit" onClick={downloadTemplate}>
          <Download className="h-4 w-4" /> Scarica modello CSV di esempio
        </button>
        {progress === null && !result && <DropZone onFile={onFile} busy={busy} />}
        {preview && !result && (
          <div className="min-w-0 space-y-3">
            <p data-testid="csv-summary" className="text-sm">
              Righe lette: <b>{preview.total}</b> · valide: <b className="text-emerald-700">{valid.length}</b> · con errori: <b className="text-red-700">{errorRows.length}</b>
            </p>
            <PreviewTable rows={preview.rows.slice(0, 10)} />
            {errorRows.some((r) => r.line > preview.rows[Math.min(9, preview.rows.length - 1)].line) && (
              <div data-testid="csv-extra-errors" className="rounded-xl bg-red-50 p-3 text-xs text-red-800">
                <b>Altre righe con errori (non verranno importate):</b>
                {errorRows.filter((r) => r.line > preview.rows[9]?.line).slice(0, 50).map((r) => <div key={r.line}>Riga {r.line}: {r.errors.join("; ")}</div>)}
              </div>
            )}
            {progress !== null && (
              <div data-testid="csv-progress" className="h-3 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-[#FFD60A] transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button className="btn-ghost-mc" disabled={progress !== null} onClick={() => setPreview(null)} data-testid="csv-change-file">Cambia file</button>
              <button data-testid="csv-confirm-import" className="btn-mc" disabled={!valid.length || progress !== null} onClick={runImport}>
                {progress !== null && <Loader2 className="h-4 w-4 animate-spin" />}
                {progress !== null ? `Importazione… ${progress}%` : `Conferma e importa ${valid.length} articoli`}
              </button>
            </div>
          </div>
        )}
        {result && (
          <div data-testid="csv-result" className="space-y-2 rounded-xl bg-muted p-4 text-sm">
            <div>Importati: <b data-testid="csv-result-created">{result.created}</b>{result.skipped > 0 && <> · già presenti: <b>{result.skipped}</b></>}</div>
            {result.errors.map((e) => <div key={e.line} className="text-red-700">Riga {e.line}: {e.error}</div>)}
            <button className="btn-mc mt-2" onClick={onClose} data-testid="csv-close">Chiudi</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
